import {
  ALL_LAYOUT_RESIZE_HANDLES,
  type LayoutResizeHandle,
} from "~/v3-poc/features/workspace/shared/layoutSettings/layoutSettings.types";
import {
  REACT_GRID_LAYOUT_SCHEMA,
  type RglSettingId,
} from "./reactGridLayoutSettings.schema";
import {
  DEFAULT_REACT_GRID_LAYOUT_SETTINGS,
  type ReactGridLayoutSettings,
} from "./reactGridLayoutSettings.types";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const booleanOr = (value: unknown, fallback: boolean): boolean =>
  typeof value === "boolean" ? value : fallback;

const numberOr = (
  value: unknown,
  fallback: number,
  min: number,
  max: number,
  integer = true,
): number =>
  typeof value === "number" &&
  Number.isFinite(value) &&
  value >= min &&
  value <= max &&
  (!integer || Number.isInteger(value))
    ? value
    : fallback;

const schemaNumberOr = (
  value: unknown,
  fallback: number,
  settingId: RglSettingId,
): number => {
  const control = REACT_GRID_LAYOUT_SCHEMA[settingId].control;
  return control.kind === "number"
    ? numberOr(value, fallback, control.min, control.max, control.integer)
    : fallback;
};

const enumOr = <T extends string>(
  value: unknown,
  options: ReadonlyArray<T>,
  fallback: T,
): T => (options.includes(value as T) ? (value as T) : fallback);

const isLegacySeOnlyHandles = (value: unknown[]): boolean =>
  value.length === 1 && value[0] === "se";

const handlesOrDefault = (
  value: unknown,
): ReadonlyArray<LayoutResizeHandle> => {
  if (!Array.isArray(value)) {
    return [...DEFAULT_REACT_GRID_LAYOUT_SETTINGS.resizeHandles];
  }
  if (isLegacySeOnlyHandles(value)) {
    return [...ALL_LAYOUT_RESIZE_HANDLES];
  }
  const selected = ALL_LAYOUT_RESIZE_HANDLES.filter((handle) =>
    value.includes(handle),
  );
  return selected.length > 0
    ? selected
    : [...DEFAULT_REACT_GRID_LAYOUT_SETTINGS.resizeHandles];
};

const nestedRecord = (
  source: Record<string, unknown>,
  key: string,
): Record<string, unknown> => (isRecord(source[key]) ? source[key] : {});

export const hydrateReactGridLayoutSettings = (
  value: unknown,
): ReactGridLayoutSettings => {
  const defaults = structuredClone(DEFAULT_REACT_GRID_LAYOUT_SETTINGS);
  if (!isRecord(value)) {
    return defaults;
  }

  const maxRows = nestedRecord(value, "maxRows");
  const globalMin = nestedRecord(value, "globalMin");
  const globalMax = nestedRecord(value, "globalMax");
  const snap = nestedRecord(value, "snap");
  const aspect = nestedRecord(value, "aspect");

  const settings: ReactGridLayoutSettings = {
    cols: schemaNumberOr(value.cols, defaults.cols, "cols"),
    rowHeight: schemaNumberOr(value.rowHeight, defaults.rowHeight, "rowHeight"),
    marginX: schemaNumberOr(value.marginX, defaults.marginX, "marginX"),
    marginY: schemaNumberOr(value.marginY, defaults.marginY, "marginY"),
    containerPaddingMode: enumOr(
      value.containerPaddingMode,
      ["margin", "custom"],
      defaults.containerPaddingMode,
    ),
    containerPaddingX: schemaNumberOr(
      value.containerPaddingX,
      defaults.containerPaddingX,
      "containerPaddingX",
    ),
    containerPaddingY: schemaNumberOr(
      value.containerPaddingY,
      defaults.containerPaddingY,
      "containerPaddingY",
    ),
    maxRows: {
      limited: booleanOr(maxRows.limited, defaults.maxRows.limited),
      value: schemaNumberOr(maxRows.value, defaults.maxRows.value, "maxRows"),
    },
    autoSize: booleanOr(value.autoSize, defaults.autoSize),
    compactor: enumOr(
      value.compactor,
      [
        "vertical",
        "horizontal",
        "none",
        "vertical-overlap",
        "horizontal-overlap",
        "none-overlap",
      ],
      defaults.compactor,
    ),
    bounded: booleanOr(value.bounded, defaults.bounded),
    dragThreshold: schemaNumberOr(
      value.dragThreshold,
      defaults.dragThreshold,
      "dragThreshold",
    ),
    resizeHandles: handlesOrDefault(value.resizeHandles),
    positionStrategy: enumOr(
      value.positionStrategy,
      ["transform", "absolute", "scaled"],
      defaults.positionStrategy,
    ),
    positionScale: schemaNumberOr(
      value.positionScale,
      defaults.positionScale,
      "positionScale",
    ),
    gridBounds: booleanOr(value.gridBounds, defaults.gridBounds),
    containerBounds: booleanOr(value.containerBounds, defaults.containerBounds),
    boundedX: booleanOr(value.boundedX, defaults.boundedX),
    boundedY: booleanOr(value.boundedY, defaults.boundedY),
    minMaxSize: booleanOr(value.minMaxSize, defaults.minMaxSize),
    globalMin: {
      enabled: booleanOr(globalMin.enabled, defaults.globalMin.enabled),
      w: schemaNumberOr(globalMin.w, defaults.globalMin.w, "globalMinW"),
      h: schemaNumberOr(globalMin.h, defaults.globalMin.h, "globalMinH"),
    },
    globalMax: {
      enabled: booleanOr(globalMax.enabled, defaults.globalMax.enabled),
      w: schemaNumberOr(globalMax.w, defaults.globalMax.w, "globalMaxW"),
      h: schemaNumberOr(globalMax.h, defaults.globalMax.h, "globalMaxH"),
    },
    snap: {
      enabled: booleanOr(snap.enabled, defaults.snap.enabled),
      x: schemaNumberOr(snap.x, defaults.snap.x, "snapX"),
      y: schemaNumberOr(snap.y, defaults.snap.y, "snapY"),
    },
    aspect: {
      enabled: booleanOr(aspect.enabled, defaults.aspect.enabled),
      ratio: schemaNumberOr(aspect.ratio, defaults.aspect.ratio, "aspectRatio"),
    },
    // External drag sources are unavailable in Layout Labs.
    externalDrop: defaults.externalDrop,
  };

  if (settings.globalMin.enabled && settings.globalMax.enabled) {
    settings.globalMax.w = Math.max(settings.globalMin.w, settings.globalMax.w);
    settings.globalMax.h = Math.max(settings.globalMin.h, settings.globalMax.h);
  }

  return settings;
};
